import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { AppConfig } from "../../../AppConfig";
import { PageRoutes } from "../../../constants/routes";
import { LocalStorageManager } from "../../../core/storage/LocalStorageManager";
import { appActions } from "../../../ducks/app";
import { Campaign } from "../../../ducks/campaign";
import { menuSelectors } from "../../../ducks/menu";
import { orderActions } from "../../../ducks/order";
import { paymentActions } from "../../../ducks/payment";
import { CouponHook } from "../../../hooks/useCoupon";
import { useMaintenance } from "../../../hooks/useMaintenance";
import { useMenu } from "../../../hooks/useMenu";
import { useNavigation } from "../../../hooks/useNavigation";
import {
  convertToISO8601,
  formatDateTime,
  getDatePlusMinutes,
  isDateTimeBefore,
} from "../../../utils/date";

export enum PaymentMethod {
  AdvancedPayment = "事前決済",
  StorePayment = "店舗でお支払い",
}

export const useOrderConfirmation = (useCoupon: CouponHook) => {
  const dispatch = useDispatch();
  const { navigateToPage } = useNavigation();
  const { storeCode, date } = useParams<{ storeCode: string; date: string }>();

  const menusInCart = useSelector(menuSelectors.menusInCart);
  const campaign = LocalStorageManager.Instance.getObject<Campaign>(
    AppConfig.Instance.LocalStorageKey["campaign"],
  );

  const {
    appliedCoupons,
    selectedAppCoupons,
    applyCoupon,
    handleRemoveCoupon,
    getCouponName,
    handleInvalidCouponAppToken,
  } = useCoupon;

  const {
    store,
    menuMapById,
    recommendedMenus,
    limitedMenus,
    searchMenus,
    handleSetFavoriteStore,
    handleSetFavoriteMenu,
  } = useMenu(useCoupon, storeCode, date);

  useEffect(() => {
    if (!store || !date) return;

    searchMenus(store.id, date);
  }, [store, date, appliedCoupons, selectedAppCoupons]);

  useEffect(() => {
    if (!store || !date) return;

    const shortestPickupDate = getDatePlusMinutes(
      new Date(),
      store.shortestTimeUntilPickup,
    );

    if (isDateTimeBefore(date, shortestPickupDate)) {
      navigateToPage(
        PageRoutes.ORDER_CONFIRMATION.replace(
          ":storeCode",
          store.storeCode,
        ).replace(":date", convertToISO8601(shortestPickupDate)),
      );
    }
  }, [store, date]);

  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState<PaymentMethod | null>(null);

  const { isPaymentMaintenance } = useMaintenance();

  // Reset payment method to store payment if payment maintenance is active
  useEffect(() => {
    if (
      isPaymentMaintenance &&
      selectedPaymentMethod === PaymentMethod.AdvancedPayment
    ) {
      setSelectedPaymentMethod(PaymentMethod.StorePayment);
    }
  }, [isPaymentMaintenance, selectedPaymentMethod]);

  const pickupDateText = useMemo(() => {
    if (!date) return "";

    return formatDateTime(new Date(date), true);
  }, [date]);

  const handleBackToMenu = useCallback(() => {
    if (!store || !date) return;

    navigateToPage(
      PageRoutes.MENU_LIST.replace(":storeCode", store.storeCode).replace(
        ":date",
        date,
      ),
    );
  }, [store, date]);

  const handlePlaceOrder = useCallback(() => {
    if (!store || !date || !menusInCart || !appliedCoupons) return;

    if (
      !!campaign &&
      menusInCart.some((menu) => !menuMapById?.get(menu.menuId)?.campaign)
    ) {
      dispatch(
        appActions.showModal.started({
          title: "割引対象外のメニューがあります",
          subTitle: "選択したメニューの一部には割引が適用されません。",
          nextButtonLabel: "閉じる",
        }),
      );
      return;
    }
    const isReservation = isDateTimeBefore(
      getDatePlusMinutes(new Date(), store.shortestTimeUntilPickup + 5),
      date,
    );

    const userMemo =
      LocalStorageManager.Instance.getObject<string>(
        AppConfig.Instance.LocalStorageKey["userMemo"],
      ) || "";

    dispatch(appActions.showLoading());
    dispatch(
      orderActions.getOrderId.started({
        store_id: store.id,
        pickup_date: date,
        total_coupons: appliedCoupons
          .filter((coupon) => coupon.isTotalCoupon)
          .map((coupon) => coupon.code),
        menus: menusInCart.map((menu) => ({
          id: menu.menuId,
          option: menu.optionId ? [menu.optionId] : [],
          quantity: menu.amount,
          coupon_code: menu.couponCode,
        })),
        memo: userMemo,
        is_advanced_payment:
          selectedPaymentMethod === PaymentMethod.AdvancedPayment ? 1 : 0,
        is_reservation: isReservation,
        early_bird: !!campaign ? 1 : undefined,
        onError: (error) => {
          console.log("error", error);
          const errorCode = error?.response?.data?.data?.code;
          // handle when app coupon is invalid and redirect to app coupon page
          if (errorCode == "0321-5021") {
            handleInvalidCouponAppToken({
              storeId: store.id,
              pickupDate: date,
              storeShortestTimeUntilPickup: store.shortestTimeUntilPickup,
              redirectUrl: window.location.href,
            });
            dispatch(appActions.hideLoading());
            return;
          }
          dispatch(appActions.hideLoading());
        },
        onSuccess: ({ orderId }) => {
          dispatch(
            orderActions.createOrder.started({
              order_id: orderId,
              // TODO: change to selected language
              lang: "ja",
              onComplete: () => dispatch(appActions.hideLoading()),
              onSuccess: () =>
                navigateToPage(
                  PageRoutes.ORDER_COMPLETE.replace(":orderId", orderId),
                ),
            }),
          );
        },
      }),
    );
    LocalStorageManager.Instance.removeObject(
      AppConfig.Instance.LocalStorageKey["userMemo"],
    );
  }, [
    store,
    date,
    selectedPaymentMethod,
    menusInCart,
    appliedCoupons,
    menuMapById,
    campaign,
  ]);

  const handlePayment = useCallback(() => {
    if (!store || !date || !menusInCart || !appliedCoupons) return;

    const isReservation = isDateTimeBefore(
      getDatePlusMinutes(new Date(), store.shortestTimeUntilPickup + 5),
      date,
    );

    const userMemo =
      LocalStorageManager.Instance.getObject<string>(
        AppConfig.Instance.LocalStorageKey["userMemo"],
      ) || "";

    dispatch(appActions.showLoading());
    dispatch(
      orderActions.getOrderId.started({
        store_id: store.id,
        pickup_date: date,
        total_coupons: appliedCoupons
          .filter((coupon) => coupon.isTotalCoupon)
          .map((coupon) => coupon.code),
        menus: menusInCart.map((menu) => ({
          id: menu.menuId,
          option: menu.optionId ? [menu.optionId] : [],
          quantity: menu.amount,
          coupon_code: menu.couponCode,
        })),
        memo: userMemo,
        is_advanced_payment:
          selectedPaymentMethod === PaymentMethod.AdvancedPayment ? 1 : 0,
        is_reservation: isReservation,
        early_bird: !!campaign ? 1 : undefined,
        onError: () => dispatch(appActions.hideLoading()),
        onSuccess: ({ orderId }) => {
          dispatch(
            paymentActions.getPaymentUrl.started({
              order_id: orderId,
              // TODO: change to selected language
              lang: "ja",
              onComplete: () => dispatch(appActions.hideLoading()),
              onSuccess: ({ url }) => (window.location.href = url),
            }),
          );
        },
      }),
    );
  }, [store, date, selectedPaymentMethod, menusInCart, appliedCoupons]);

  return {
    store,
    date,
    handleSetFavoriteStore,
    menuMapById,
    pickupDateText,
    recommendedMenus,
    limitedMenus,
    selectedPaymentMethod,
    setSelectedPaymentMethod,
    handleSetFavoriteMenu,
    handleBackToMenu,
    handlePlaceOrder,
    handlePayment,
    appliedCoupons,
    applyCoupon,
    getCouponName,
    handleRemoveCoupon,
  };
};
